import { Container, Typography, Box, Card, CardContent, Pagination } from "@mui/material";
import useSWR from 'swr';
import { PATH_NAME } from "../../../configs/pathName";
import { generalFetcher, generalItemFetcher } from "../../../helpers/Fetchers/fetchers";
import { IOrder, IPayment } from "../../../models/IWallet";
import { format } from "date-fns";
import { OdersPageStyles } from '../../../styles/ProtectedPage/orders';
import { Link } from 'react-router-dom';
import { typo } from "../../../assets/fonts/font";
import colorConfigs from "../../../configs/colorConfigs";
import { useState } from "react";

const OrdersPage = () => {
  const [page, setPage] = useState<number>(1);
  const items_per_page = 5;
  const { data: userProfile } = useSWR(PATH_NAME.API_AUTH_PROFILE, generalFetcher);
  const { data: orderHistory } = useSWR(
    userProfile ? PATH_NAME.API_GET_ORDER_BY_USER_ID : null,
    generalItemFetcher
  );

  if (!userProfile || !orderHistory) {
    return <div>Loading...</div>;
  }

  const totalSpend = orderHistory.reduce((sum: number, order: IOrder) => {
    return sum + order.payment_amount;
  }, 0);

  const groupedOrders = orderHistory.reduce((groups: Record<string, IOrder[]>, order: IOrder) => {
    const orderDate = new Date(order.created_at);
    let groupKey = format(orderDate, "dd MMMM yyyy");

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(order);

    return groups;
  }, {});

  const sortedDates = Object.keys(groupedOrders).sort((b, a) => new Date(a).getTime() - new Date(b).getTime());
  const sortedOrders = sortedDates.flatMap((date) => groupedOrders[date]);
  const totalPages = Math.max(1, Math.ceil(sortedOrders.length / items_per_page));
  const paginatedOrders = sortedOrders.slice(
    (page - 1) * items_per_page,
    page * items_per_page
  );
  const paginatedOrdersByDate = paginatedOrders.reduce((groups: Record<string, IOrder[]>, order: IOrder) => {
    const orderDate = format(new Date(order.created_at), "dd MMMM yyyy");

    if (!groups[orderDate]) {
      groups[orderDate] = [];
    }
    groups[orderDate].push(order);

    return groups;
  }, {});
  const paginatedDates = sortedDates.filter((date) => paginatedOrdersByDate[date]);

  return (
    <Container>
      <Box sx={OdersPageStyles.containerBox}>
        <Card sx={OdersPageStyles.card}>
          <CardContent sx={OdersPageStyles.cardContentLeft}>
            <Typography variant="h6" sx={{ ...typo.title, color: colorConfigs.primary }}>{orderHistory.length}</Typography>
            <Typography variant="subtitle1" sx={typo.bodyContent}>Total Orders</Typography> 
          </CardContent>
        </Card>

        <Card sx={OdersPageStyles.card}>
          <CardContent sx={OdersPageStyles.cardContentLeft}>
            <Typography variant="h6" sx={{ ...typo.title, color: colorConfigs.primary }}>SGD {totalSpend.toFixed(2)}</Typography>
            <Typography variant="subtitle1" sx={typo.bodyContent}>Total Spend</Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={OdersPageStyles.transactionBox}>
        <Typography variant="h6" gutterBottom sx={typo.title}>Transactions History</Typography>
        <Box sx={OdersPageStyles.transactionList}>
          {paginatedDates.map((date) => (
            <Box key={date} sx={OdersPageStyles.transactionDateBox}>
              <Typography variant="subtitle1" sx={{...typo.bodyContent,...OdersPageStyles.transactionDate}}>
                {date}
              </Typography>

              {paginatedOrdersByDate[date].map((order: IOrder) => (
                <Link key={order.id} to={`${PATH_NAME.ORDER_DETAILS}${order.id}`} style={OdersPageStyles.orderLink}>
                  <Card sx={OdersPageStyles.orderCard}>
                    <Box sx={OdersPageStyles.orderDetailsBox}>
                      <Box>
                        <Typography variant="h6" sx={{...typo.bodyContent , color: colorConfigs.primary}}>
                          Order {order.id}
                        </Typography>
                        <Typography variant="body2" sx={{...typo.bodyContent,...OdersPageStyles.orderItemsText}}>
                          Items: {order.items.length}
                        </Typography>
                      </Box>
                      <Box sx={OdersPageStyles.paymentAmountText}>
                        <Typography variant="body1" sx={{...typo.bodyContent, color: colorConfigs.primary}}>SGD {order.total.toFixed(2)}</Typography>
                        <Typography variant="body2" sx={{...typo.bodyContent,...OdersPageStyles.paymentDetailsText}}>
                          {order.payments.map((p: IPayment) => p.type).join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Link>
              ))}
            </Box>
          ))}
        </Box>
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              sx={{
                "& .MuiPaginationItem-root": {
                  color: colorConfigs.tertiary,
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  color: colorConfigs.secondary,
                  backgroundColor: colorConfigs.primary,
                },
                "& .MuiPaginationItem-root.Mui-selected:hover, & .MuiPaginationItem-root.Mui-selected:focus-visible": {
                  color: colorConfigs.secondary,
                  backgroundColor: colorConfigs.primary,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default OrdersPage;